import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Input from "../../../components/BaseUI/Input";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  FiZap,
  FiTrash2,
  FiStopCircle,
  FiSearch,
  FiChevronDown,
  FiCheck,
  FiArrowLeft,
  FiArrowRight,
  FiLink2,
  FiCpu,
  FiCheckCircle,
  FiRefreshCw,
  FiChevronRight,
  FiEdit2,
  FiUser,
  FiWifi,
  FiWifiOff,
  FiActivity,
} from "react-icons/fi";
import Pagination from "../../../components/BaseUI/Pagination";
import { withAuth } from "../../../components/HOC/withAuth";
import { formatDateTimeTrInAppTz, dateStrInAppTz } from "../../../utils/appTime";
import {
  filterMachinesByAccess,
  isMachineAllowed,
} from "../../../utils/machineAccess";
import styles from "./EPC.module.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const VIEWS = {
  MACHINES: "machines",
  ACTIONS: "actions",
  FORM: "form",
  SUCCESS: "success",
};

const ACTIONS = [
  {
    id: "production",
    title: "Üretim Girişi",
    desc: "Makine üzerinde gerçekleştirilen üretim miktarını kaydedin",
    icon: <FiZap size={32} />,
    color: "var(--m-primary, #00b4f3)",
    bg: "rgba(0,151,178,0.08)",
  },
  {
    id: "scrap",
    title: "Hurda Girişi",
    desc: "Vardiya hurda ve kalite verilerini kaydedin",
    icon: <FiTrash2 size={32} />,
    color: "var(--m-danger, #ca371c)",
    bg: "rgba(202,55,28,0.08)",
  },
  {
    id: "stop",
    title: "Duruş Girişi",
    desc: "Mevcut duruşa neden atayın veya planlı duruş ekleyin",
    icon: <FiStopCircle size={32} />,
    color: "#d97706",
    bg: "rgba(217,119,6,0.08)",
  },
  {
    id: "part_select",
    title: "Parça Seçimi",
    desc: "Makinenin ürettiği aktif parçayı/ürünü seçin",
    icon: <FiEdit2 size={32} />,
    color: "#4f46e5",
    bg: "rgba(79, 70, 229, 0.08)",
  },
];

// ─── helpers ──────────────────────────────────────────────────────────────────
const todayStr = () => dateStrInAppTz(new Date());

const formatTimeSince = (date) => {
  if (!date) return "hiç";
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);
  if (seconds < 60) return `${seconds}sn önce`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}dk önce`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}sa önce`;
  return `${Math.floor(seconds / 86400)}g önce`;
};
// ─── StyledSelect ─────────────────────────────────────────────────────────────
function StyledSelect({ value, onChange, options, placeholder }) {
  return (
    <div className={styles.sel}>
      <select value={value} onChange={onChange}>
        <option value="">{placeholder || "— Seçiniz —"}</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// ─── SearchableSelect ─────────────────────────────────────────────────────────
function SearchableSelect({ value, onChange, options, placeholder }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const filtered = options.filter((o) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      o.label.toLowerCase().includes(q) ||
      (o.sub || "").toLowerCase().includes(q)
    );
  });

  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (!e.target.closest(`.${styles.srchWrap}`)) setOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  return (
    <div className={styles.srchWrap}>
      <button
        type="button"
        className={`${styles.srchBtn} ${open ? styles.srchOpen : ""} ${value ? styles.srchFilled : ""}`}
        onClick={() => setOpen((v) => !v)}
      >
        {selected ? (
          <span className={styles.srchVal}>
            <strong>{selected.label}</strong>
            {selected.sub && <em>{selected.sub}</em>}
          </span>
        ) : (
          <span className={styles.srchPlaceholder}>
            {placeholder || "— Seçiniz —"}
          </span>
        )}
        <FiChevronDown
          className={`${styles.srchChev} ${open ? styles.rotated : ""}`}
          size={14}
        />
      </button>

      {open && (
        <div className={styles.srchPanel}>
          <div className={styles.srchInput}>
            <FiSearch size={13} />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Ara..."
            />
          </div>
          <div className={styles.srchList}>
            {filtered.length === 0 ? (
              <div className={styles.srchEmpty}>Sonuç yok</div>
            ) : (
              filtered.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`${styles.srchItem} ${value === o.value ? styles.srchSelected : ""}`}
                  onClick={() => {
                    onChange(o.value);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <span className={styles.srchItemMain}>{o.label}</span>
                  {o.sub && <span className={styles.srchItemSub}>{o.sub}</span>}
                  {value === o.value && (
                    <FiCheck size={13} className={styles.srchCheck} />
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Field ────────────────────────────────────────────────────────────────────
function Field({ label, required, children }) {
  return (
    <div className={styles.field}>
      <label className={styles.fieldLabel}>
        {label}
        {required && <span className={styles.req}>*</span>}
      </label>
      {children}
    </div>
  );
}

// ─── NativeInput ──────────────────────────────────────────────────────────────
function NativeInput({ type = "text", value, onChange, placeholder, min }) {
  return (
    <input
      type={type}
      className={styles.native}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      min={min}
    />
  );
}

// ─── MachineHeader (shown on action + form views) ─────────────────────────────
function MachineHeader({ machine, step, onBack }) {
  return (
    <div className={styles.machineHeader}>
      <button type="button" className={styles.backBtn} onClick={onBack}>
        <FiArrowLeft size={16} />
        <span>Geri</span>
      </button>
      <div className={styles.machineInfo}>
        <span className={styles.machineIcon}>
          <FiCpu size={15} />
        </span>
        <span className={styles.machineName}>{machine.name}</span>
        {machine.code && (
          <span className={styles.machineCode}>{machine.code}</span>
        )}
      </div>
      <div className={styles.breadcrumb}>
        <span className={step >= 1 ? styles.bcActive : styles.bcInactive}>
          Makine
        </span>
        <FiChevronRight size={12} className={styles.bcSep} />
        <span className={step >= 2 ? styles.bcActive : styles.bcInactive}>
          İşlem
        </span>
        {step >= 3 && (
          <>
            <FiChevronRight size={12} className={styles.bcSep} />
            <span className={styles.bcActive}>Form</span>
          </>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// VIEW: Machines (Card grid layout with search and quick actions)
// ═══════════════════════════════════════════════════════════════════════════════
function MachinesView({
  machines,
  loading,
  onSelectActionDirectly,
  pagination,
  onPageChange,
  searchQuery,
  onSearchChange,
  emptyMessage,
  onRedirectToDashboard,
}) {
  if (loading) {
    return (
      <div className={styles.loadingForm}>
        <FiRefreshCw className={styles.spin} size={22} />
        <span>Makineler yükleniyor...</span>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.searchBarContainer}>
        <div className={styles.searchBarInputWrap}>
          <div className={styles.searchBarIcon}>
            <FiSearch size={16} />
          </div>
          <input
            type="text"
            className={styles.searchBarInput}
            placeholder="Makine adı veya koduna göre ara..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>

      {machines.length === 0 ? (
        <div className={styles.noMachinesFound}>
          {emptyMessage || "Aramanızla eşleşen makine bulunamadı."}
        </div>
      ) : (
        <>
          <div className={styles.machineGrid}>
            {machines.map((m) => {
              const allowedActions = ACTIONS.filter(
                (a) => a.id !== "part_select" || m.entryType === "epc",
              );

              return (
                <div
                  key={m._id}
                  className={styles.machineCard}
                  onClick={() => onRedirectToDashboard?.(m)}
                >
                  <div>
                    <div
                      className={styles.machineCardHeader}
                      style={{ display: "flex", alignItems: "center" }}
                    >
                      <div className={styles.machineCardIconWrap}>
                        <FiCpu size={18} />
                      </div>
                      <div
                        style={{
                          marginLeft: "auto",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {m.code && (
                          <span className={styles.machineCardCode}>
                            {m.code}
                          </span>
                        )}
                        <div
                          title={
                            m.status === "online" ? "Çevrimiçi" : "Çevrimdışı"
                          }
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            padding: "4px 8px",
                            borderRadius: "6px",
                            marginLeft: "8px",
                            backgroundColor:
                              m.status === "online"
                                ? "rgba(16, 185, 129, 0.15)"
                                : "rgba(202, 55, 28, 0.15)",
                            color:
                              m.status === "online"
                                ? "var(--m-success, #10b981)"
                                : "var(--m-danger, #ca371c)",
                          }}
                        >
                          {m.status === "online" ? (
                            <FiWifi size={14} />
                          ) : (
                            <FiWifiOff size={14} />
                          )}
                        </div>
                      </div>
                    </div>
                    <div className={styles.machineCardBody}>
                      <h3 className={styles.machineCardName}>{m.name}</h3>
                      <div
                        className={
                          m.productTypes && m.productTypes.length > 1
                            ? styles.producingPartText
                            : styles.producingPartTextSingle
                        }
                        title={
                          m.productTypesDetail?.length > 1
                            ? m.productTypesDetail
                                .map((d) => d.code || d.name)
                                .join(", ")
                            : undefined
                        }
                      >
                        Üretilen Parça:{" "}
                        {m.productTypes && m.productTypes.length > 1
                          ? `${m.productTypes.length} Parça`
                          : m.productTypesDetail?.length === 1
                            ? m.productTypesDetail[0].code ||
                              m.productTypesDetail[0].name
                            : m.productTypeCode ||
                              (m.productTypesDetail?.[0]?.code) ||
                              "Yok"}
                      </div>
                    </div>
                  </div>

                  <div className={styles.machineCardActions}>
                    <div
                      style={{
                        marginTop: "12px",
                        fontSize: "12px",
                        color: "var(--text-secondary, #64748b)",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                      }}
                    >
                      <FiUser size={12} />
                      <span>
                        Son İşlem:{" "}
                        {m.lastActivity && (
                          <strong>
                            {m.lastActivity.user?.name ||
                              m.lastActivity.user?.email ||
                              "-"}
                          </strong>
                        )}{" "}
                        {m.lastActivity && (
                          <span>
                            ({formatTimeSince(m.lastActivity.timestamp)})
                          </span>
                        )}
                      </span>
                    </div>
                    <div style={{ display: "flex", gap: "4px" }}>
                      <button
                        type="button"
                        className={styles.miniActionBtn}
                        style={{
                          "--btn-bg": "rgba(99, 102, 241, 0.08)",
                          "--btn-color": "#6366f1",
                          "--shadow-color": "#6366f126",
                        }}
                        data-tooltip="Dashboard'a Git"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onRedirectToDashboard) onRedirectToDashboard(m);
                        }}
                      >
                        <FiActivity size={15} />
                      </button>
                      <span
                        style={{
                          color: "var(--text-secondary, #64748b)",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {" "}
                        |{" "}
                      </span>
                      {allowedActions.map((a) => (
                        <button
                          key={a.id}
                          type="button"
                          className={styles.miniActionBtn}
                          style={{
                            "--btn-bg": a.bg,
                            "--btn-color": a.color,
                            "--shadow-color": a.color + "26",
                          }}
                          data-tooltip={a.title}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSelectActionDirectly(m, a.id);
                          }}
                        >
                          {a.id === "production" && <FiZap size={15} />}
                          {a.id === "scrap" && <FiTrash2 size={15} />}
                          {a.id === "stop" && <FiStopCircle size={15} />}
                          {a.id === "part_select" && <FiEdit2 size={15} />}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {pagination && pagination.total > pagination.limit && (
            <div style={{ marginTop: 24 }}>
              <Pagination
                totalItems={pagination.total}
                totalPerPage={pagination.limit}
                currentPage={pagination.page}
                options={{
                  selectQuantity: false,
                  simpleCounting: false,
                  skipPrevious: true,
                  skipNext: true,
                  previous: true,
                  next: true,
                }}
                onPageChange={({ pagination: pInfo }) => {
                  onPageChange(pInfo.currentPage);
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// VIEW: Actions (card grid)
// ═══════════════════════════════════════════════════════════════════════════════
function ActionsView({ machine, onSelect, onBack }) {
  const filteredActions = ACTIONS.filter(
    (a) => a.id !== "part_select" || machine.entryType === "epc",
  );
  return (
    <>
      <MachineHeader machine={machine} step={2} onBack={onBack} />
      <div className={styles.actionGrid}>
        {filteredActions.map((a) => (
          <button
            key={a.id}
            type="button"
            className={styles.actionCard}
            onClick={() => onSelect(a.id)}
          >
            <div
              className={styles.actionIconWrap}
              style={{ background: a.bg, color: a.color }}
            >
              {a.icon}
            </div>
            <div className={styles.actionCardBody}>
              <div className={styles.actionTitle} style={{ color: a.color }}>
                {a.title}
              </div>
              <div className={styles.actionDesc}>{a.desc}</div>
            </div>
            <div className={styles.actionArrow} style={{ color: a.color }}>
              <FiArrowRight size={18} />
            </div>
          </button>
        ))}
      </div>
    </>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// VIEW: Production Form
// ═══════════════════════════════════════════════════════════════════════════════
function ProductionForm({ machine, formData, onSubmit, saving, onBack }) {
  const [partId, setPartId] = useState("");
  const [qty, setQty] = useState("");
  const [date, setDate] = useState(todayStr());

  const opts = [
    ...new Map(
      (formData?.productionOptions || []).map((o) => [
        o.partId,
        { value: o.partId, label: o.partName, sub: o.partCode },
      ]),
    ).values(),
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      partId,
      machineId: machine._id,
      producedNow: Number(qty),
      date,
    });
  };

  return (
    <form className={styles.formCard} onSubmit={handleSubmit}>
      <div
        className={styles.formCardHeader}
        style={{ "--accent": "var(--m-primary, #00b4f3)" }}
      >
        <span className={styles.formCardIcon}>
          <FiZap size={18} />
        </span>
        <div>
          <div className={styles.formCardTitle}>Üretim Girişi</div>
          <div className={styles.formCardSub}>{machine.name}</div>
        </div>
      </div>

      <div className={`${styles.formBody} ${styles.formBodyHorizontal}`}>
        <div className={styles.formRow}>
          <Field label="Üretilen Parça / Ürün" required>
            {opts.length === 0 ? (
              <div className={styles.emptyOpts}>
                Bu makine için tanımlı üretim hattı yok
              </div>
            ) : (
              <SearchableSelect
                value={partId}
                onChange={setPartId}
                options={opts}
                placeholder="— Parça Seçiniz —"
              />
            )}
          </Field>

          <Field label="Üretim Miktarı (adet)" required>
            <NativeInput
              type="number"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              placeholder="0"
              min="1"
            />
          </Field>

          <Field label="Tarih" required>
            <NativeInput
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
        </div>
      </div>

      <div className={styles.formFooter}>
        <Button
          appearance="danger-subtle"
          type="button"
          onClick={onBack}
          icon={<FiArrowLeft />}
        >
          İptal
        </Button>
        <Button
          appearance="primary-animated"
          type="submit"
          disabled={saving || !partId || !qty || Number(qty) <= 0 || !date}
          icon={<FiZap />}
        >
          {saving ? "Kaydediliyor..." : "Üretimi Kaydet"}
        </Button>
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// VIEW: Scrap Form
// ═══════════════════════════════════════════════════════════════════════════════
function ScrapForm({
  machine,
  formData,
  authHeaders,
  initialShiftId,
  initialPartId,
  onSubmit,
  saving,
  onBack,
}) {
  const [partId, setPartId] = useState("");
  const [producedParts, setProducedParts] = useState([]);
  const [shift, setShift] = useState("");
  const [scrapGroup, setScrapGroup] = useState("");
  const [date, setDate] = useState(todayStr());
  const [totalProduced, setTotalProduced] = useState(0);
  const [loadingProduced, setLoadingProduced] = useState(false);
  const [currentRejected, setCurrentRejected] = useState(0);
  const [addRejected, setAddRejected] = useState("");
  const [notes, setNotes] = useState("");

  const shiftOpts = (formData?.shifts || []).map((s) => ({
    value: s._id,
    label: s.name,
  }));

  const scrapGroupOpts = (formData?.scrapGroups || []).map((g) => ({
    value: g._id,
    label: g.name,
    sub: g.description || "",
  }));

  const partOpts = producedParts.map((p) => ({
    value: p.partId,
    label: p.partName,
    sub: `${p.partCode} (${p.quantity} adet)`,
  }));

  const fetchScrapContext = useCallback(
    async (dateVal, shiftVal, partIdVal) => {
      setLoadingProduced(true);
      try {
        const params = new URLSearchParams({
          machineId: machine._id,
          action: "scrap",
          date: dateVal,
        });
        if (shiftVal) params.set("shift", shiftVal);
        if (partIdVal) params.set("partId", partIdVal);

        const { data } = await axios.get(
          `${API_URL}/api/epc/form-data?${params}`,
          { headers: authHeaders() },
        );

        const partsList = data.producedParts || [];
        setProducedParts(partsList);

        if (partIdVal && !partsList.some((p) => p.partId === partIdVal)) {
          setPartId("");
          setTotalProduced(0);
          setCurrentRejected(0);
          setScrapGroup("");
        } else {
          setTotalProduced(data.totalProduced ?? 0);
          const existing = data.existingScrapEntry;
          if (existing) {
            const groupId =
              existing.scrapGroup?._id || existing.scrapGroup || "";
            setScrapGroup(groupId ? String(groupId) : "");
            setCurrentRejected(Math.max(0, Number(existing.rejected) || 0));
          } else {
            setScrapGroup("");
            setCurrentRejected(0);
          }
        }
        setAddRejected("");
        setNotes("");
      } catch {
        setTotalProduced(0);
      } finally {
        setLoadingProduced(false);
      }
    },
    [machine._id, authHeaders],
  );

  useEffect(() => {
    if (date) {
      fetchScrapContext(date, shift, partId);
    }
  }, [date, shift, partId, fetchScrapContext]);

  useEffect(() => {
    if (initialShiftId && !shift) {
      setShift(String(initialShiftId));
    }
  }, [initialShiftId, shift]);

  useEffect(() => {
    if (!initialPartId || partId) return;
    setPartId(String(initialPartId));
  }, [initialPartId, partId]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const addAmount = Math.max(0, Number(addRejected) || 0);
    if (currentRejected + addAmount > totalProduced) return;
    onSubmit({
      machine: machine._id,
      shift: shift || undefined,
      scrapGroup,
      part: partId,
      date,
      rejected: addAmount,
      notes,
    });
  };

  return (
    <form className={styles.formCard} onSubmit={handleSubmit}>
      <div
        className={styles.formCardHeader}
        style={{ "--accent": "var(--m-danger, #ca371c)" }}
      >
        <span
          className={styles.formCardIcon}
          style={{
            background: "rgba(202,55,28,0.12)",
            color: "var(--m-danger, #ca371c)",
          }}
        >
          <FiTrash2 size={18} />
        </span>
        <div>
          <div
            className={styles.formCardTitle}
            style={{ color: "var(--m-danger, #ca371c)" }}
          >
            Hurda Girişi
          </div>
          <div className={styles.formCardSub}>{machine.name}</div>
        </div>
      </div>

      <div className={styles.formBody}>
        <div className={styles.formRow}>
          <Field label="Üretilen Parça / Ürün" required>
            {partOpts.length === 0 ? (
              <div className={styles.emptyOpts}>
                Bu gün/vardiya için kaydedilmiş üretim bulunamadı
              </div>
            ) : (
              <SearchableSelect
                value={partId}
                onChange={setPartId}
                options={partOpts}
                placeholder="— Parça Seçiniz —"
              />
            )}
          </Field>
        </div>
        <div className={styles.formRow}>
          <Field label="Vardiya">
            <StyledSelect
              value={shift}
              onChange={(e) => setShift(e.target.value)}
              options={shiftOpts}
              placeholder="— Vardiya Seçiniz —"
            />
          </Field>
          <Field label="Tarih" required>
            <NativeInput
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
        </div>
        <div className={styles.formRow}>
          <Field label="Hurda Grubu" required>
            {scrapGroupOpts.length === 0 ? (
              <div className={styles.emptyOpts}>Tanımlı hurda grubu yok</div>
            ) : (
              <SearchableSelect
                value={scrapGroup}
                onChange={setScrapGroup}
                options={scrapGroupOpts}
                placeholder="— Hurda Grubu Seçiniz —"
              />
            )}
          </Field>
          <Field label="Eklenecek Hurda" required>
            <NativeInput
              type="number"
              value={addRejected}
              onChange={(e) => setAddRejected(e.target.value)}
              placeholder="0"
              min="1"
            />
          </Field>
        </div>
        <div className={styles.formRow}>
          <Field label="Mevcut Hurda">
            <div className={styles.readonlyValue}>
              <strong>{currentRejected}</strong>
              <span className={styles.readonlyUnit}>adet</span>
            </div>
          </Field>
          <Field label="Günlük Üretim">
            <div className={styles.readonlyValue}>
              {loadingProduced ? (
                <span className={styles.readonlyLoading}>Hesaplanıyor...</span>
              ) : (
                <strong>{totalProduced}</strong>
              )}
              <span className={styles.readonlyUnit}>adet</span>
            </div>
          </Field>
          <Field label="Not">
            <NativeInput
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="İsteğe bağlı not..."
            />
          </Field>
        </div>
      </div>

      <div className={styles.formFooter}>
        <Button
          appearance="danger-subtle"
          type="button"
          onClick={onBack}
          icon={<FiArrowLeft />}
        >
          İptal
        </Button>
        <Button
          appearance="primary-animated"
          type="submit"
          disabled={
            saving ||
            !date ||
            !partId ||
            !scrapGroup ||
            scrapGroupOpts.length === 0 ||
            !addRejected ||
            Number(addRejected) <= 0 ||
            currentRejected + Number(addRejected) > totalProduced
          }
          icon={<FiTrash2 />}
        >
          {saving ? "Kaydediliyor..." : "Hurda Kaydet"}
        </Button>
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// VIEW: Stop Form
// ═══════════════════════════════════════════════════════════════════════════════
function formatStopDuration(log) {
  if (log.durationMinutes != null) return `${log.durationMinutes} dk`;
  if (log.endTime && log.startTime) {
    const mins = Math.round(
      (new Date(log.endTime) - new Date(log.startTime)) / 60000,
    );
    return `${mins} dk`;
  }
  return "Devam ediyor";
}

function StopForm({
  machine,
  formData,
  initialStopLogId,
  onSubmit,
  saving,
  onBack,
  onDateChange,
  loadingStops,
}) {
  const PLANNED_ID = "__planned__";
  const [stopLogId, setStopLogId] = useState("");
  const [stopReason, setStopReason] = useState("");
  const [notes, setNotes] = useState("");
  const [date, setDate] = useState(formData?.date || todayStr());
  const [plannedStart, setPlannedStart] = useState(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [plannedEnd, setPlannedEnd] = useState("");
  const [plannedDuration, setPlannedDuration] = useState("");

  const pendingStops = formData?.pendingStops || [];
  const isPlanned = stopLogId === PLANNED_ID;

  const stopOpts = [
    {
      value: PLANNED_ID,
      label: "Planlı duruş ekle",
      sub: "Yeni duruş kaydı oluştur",
    },
    ...pendingStops.map((s) => ({
      value: s._id,
      label: formatDateTimeTrInAppTz(s.startTime),
      sub:
        formatStopDuration(s) +
        (s.stopReason ? ` (${s.stopReason.name})` : " (Nedensiz)"),
    })),
  ];

  const srOpts = (formData?.stopReasons || []).map((r) => ({
    value: r._id,
    label: r.name,
    sub: r.oeeGroup?.name || "",
  }));

  const selectedStop = !isPlanned
    ? pendingStops.find((s) => s._id === stopLogId)
    : null;

  useEffect(() => {
    if (formData?.date && formData.date !== date) {
      setDate(formData.date);
    }
  }, [formData?.date, date]);

  useEffect(() => {
    if (!initialStopLogId || !pendingStops.length) return;
    const id = String(initialStopLogId);
    const match = pendingStops.find((s) => String(s._id) === id);
    if (match) {
      setStopLogId(id);
      const reasonId = match.stopReason?._id || match.stopReason;
      if (reasonId) setStopReason(String(reasonId));
    }
  }, [initialStopLogId, pendingStops]);

  const durationNum =
    plannedDuration !== "" && plannedDuration != null
      ? Number(plannedDuration)
      : null;
  const durationOk =
    plannedDuration === "" ||
    plannedDuration == null ||
    (Number.isFinite(durationNum) && durationNum > 0);

  const canSubmit =
    !!stopReason &&
    (isPlanned
      ? !!plannedStart &&
        (!plannedEnd || new Date(plannedEnd) > new Date(plannedStart)) &&
        durationOk
      : !!stopLogId);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    if (isPlanned) {
      onSubmit(
        {
          machineId: machine._id,
          startTime: new Date(plannedStart).toISOString(),
          endTime: plannedEnd ? new Date(plannedEnd).toISOString() : undefined,
          durationMinutes:
            durationNum != null && Number.isFinite(durationNum) && durationNum > 0
              ? durationNum
              : undefined,
          stopReason,
          notes,
        },
        "planned-stop",
      );
      return;
    }
    onSubmit({
      stopLogId,
      stopReason,
      notes,
    });
  };

  return (
    <form className={styles.formCard} onSubmit={handleSubmit}>
      <div className={styles.formCardHeader} style={{ "--accent": "#d97706" }}>
        <span
          className={styles.formCardIcon}
          style={{ background: "rgba(217,119,6,0.12)", color: "#d97706" }}
        >
          <FiStopCircle size={18} />
        </span>
        <div>
          <div className={styles.formCardTitle} style={{ color: "#d97706" }}>
            Duruş Kaydı
          </div>
          <div className={styles.formCardSub}>{machine.name}</div>
        </div>
      </div>

      <div className={styles.formBody}>
        <Field label="Tarih">
          <NativeInput
            type="date"
            value={date}
            onChange={(e) => {
              const next = e.target.value;
              setDate(next);
              setStopLogId("");
              onDateChange?.(next);
            }}
          />
        </Field>

        <Field label="Duruş Kaydı" required>
          <SearchableSelect
            value={stopLogId}
            onChange={setStopLogId}
            options={stopOpts}
            placeholder={
              loadingStops ? "Duruşlar yükleniyor..." : "— Duruş Seçiniz —"
            }
          />
        </Field>

        {isPlanned && (
          <>
            <Field label="Başlangıç" required>
              <NativeInput
                type="datetime-local"
                value={plannedStart}
                onChange={(e) => setPlannedStart(e.target.value)}
              />
            </Field>
            <Field label="Bitiş">
              <NativeInput
                type="datetime-local"
                value={plannedEnd}
                onChange={(e) => setPlannedEnd(e.target.value)}
              />
            </Field>
            <Field label="Süre (dakika)">
              <NativeInput
                type="number"
                min="1"
                value={plannedDuration}
                onChange={(e) => setPlannedDuration(e.target.value)}
                placeholder="Bitiş girilmezse manuel süre"
              />
            </Field>
          </>
        )}

        {selectedStop && (
          <div className={styles.stopPreview}>
            <div className={styles.stopPreviewItem}>
              <span>Başlangıç</span>
              <strong>{formatDateTimeTrInAppTz(selectedStop.startTime)}</strong>
            </div>
            <div className={styles.stopPreviewItem}>
              <span>Süre</span>
              <strong>{formatStopDuration(selectedStop)}</strong>
            </div>
            {selectedStop.endTime && (
              <div className={styles.stopPreviewItem}>
                <span>Bitiş</span>
                <strong>{formatDateTimeTrInAppTz(selectedStop.endTime)}</strong>
              </div>
            )}
          </div>
        )}

        {!!stopLogId && (
          <>
            <Field label="Duruş Nedeni" required>
              <SearchableSelect
                value={stopReason}
                onChange={setStopReason}
                options={srOpts}
                placeholder="— Neden Seçiniz —"
              />
            </Field>

            <Field label="Not">
              <NativeInput
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="İsteğe bağlı not..."
              />
            </Field>
          </>
        )}
      </div>

      <div className={styles.formFooter}>
        <Button
          appearance="subtle"
          type="button"
          onClick={onBack}
          icon={<FiArrowLeft />}
        >
          İptal
        </Button>
        <Button
          appearance="primary-animated"
          type="submit"
          disabled={saving || !canSubmit}
          icon={<FiStopCircle />}
        >
          {saving
            ? "Kaydediliyor..."
            : isPlanned
              ? "Planlı Duruş Kaydet"
              : "Nedeni Ata"}
        </Button>
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// VIEW: Part Select Form
// ═══════════════════════════════════════════════════════════════════════════════
function PartSelectForm({ machine, formData, onSubmit, saving, onBack }) {
  const [selectedIds, setSelectedIds] = useState(() => {
    const fromArr = Array.isArray(formData?.currentProductTypes)
      ? formData.currentProductTypes
      : formData?.currentProductType
        ? [formData.currentProductType]
        : [];
    return fromArr.map((x) => String(x || "").trim()).filter(Boolean);
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);

  const parts = formData?.parts || [];
  const filteredParts = searchQuery.trim()
    ? parts.filter((p) => {
        const q = searchQuery.toLowerCase();
        return (
          String(p.code || "")
            .toLowerCase()
            .includes(q) ||
          String(p.name || "")
            .toLowerCase()
            .includes(q)
        );
      })
    : parts;

  const PAGE_SIZE = 10;
  const totalItems = filteredParts.length;
  const pageCount = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const safePage = Math.min(Math.max(1, page), pageCount);
  const displayedParts = filteredParts.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  const partIdOf = (p) => String(p?._id || "").trim();

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((c) => c !== id);
      }
      if (prev.length >= 2) {
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      machineId: machine._id,
      productTypes: selectedIds,
      productType: selectedIds.length > 0 ? selectedIds[0] : null,
    });
  };

  return (
    <form className={styles.formCard} onSubmit={handleFormSubmit}>
      <div className={styles.formCardHeader} style={{ "--accent": "#4f46e5" }}>
        <span
          className={styles.formCardIcon}
          style={{ background: "rgba(79, 70, 229, 0.12)", color: "#4f46e5" }}
        >
          <FiEdit2 size={18} />
        </span>
        <div>
          <div className={styles.formCardTitle} style={{ color: "#4f46e5" }}>
            Parça Seçimi
          </div>
          <div className={styles.formCardSub}>{machine.name}</div>
        </div>
      </div>

      <div className={styles.formBody}>
        <div style={{ marginBottom: 15 }}>
          <span
            style={{
              fontSize: 13,
              fontWeight: 600,
              display: "block",
              marginBottom: 6,
            }}
          >
            Seçili Parçalar (En fazla 2 adet seçilebilir)
          </span>
          {selectedIds.length === 0 ? (
            <span
              style={{
                fontSize: 13,
                color: "rgba(0,0,0,0.45)",
                fontStyle: "italic",
              }}
            >
              Parça seçilmedi
            </span>
          ) : (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {selectedIds.map((id) => {
                const part = parts.find((p) => partIdOf(p) === String(id));
                const label = part
                  ? `${part.code} — ${part.name || ""}`
                  : id;
                return (
                  <span
                    key={id}
                    style={{
                      background: "rgba(79, 70, 229, 0.08)",
                      color: "#4f46e5",
                      border: "1px solid rgba(79, 70, 229, 0.2)",
                      borderRadius: 16,
                      padding: "4px 12px",
                      fontSize: 12,
                      fontWeight: 600,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <span>{label}</span>
                    <button
                      type="button"
                      onClick={() => toggleSelect(id)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "#4f46e5",
                        cursor: "pointer",
                        fontWeight: "bold",
                        padding: 0,
                        fontSize: 14,
                        lineHeight: 1,
                      }}
                    >
                      &times;
                    </button>
                  </span>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ position: "relative", marginBottom: 15 }}>
          <div
            style={{
              position: "absolute",
              left: 10,
              top: "50%",
              transform: "translateY(-50%)",
              color: "rgba(0,0,0,0.4)",
              display: "flex",
              alignItems: "center",
            }}
          >
            <FiSearch size={16} />
          </div>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Parça kodu veya adı ile ara..."
            style={{
              width: "100%",
              padding: "8px 12px 8px 32px",
              border: "1px solid #d0d5dd",
              borderRadius: 6,
              fontSize: 13,
              background: "var(--m-card-background-color, #fff)",
              color: "inherit",
              outline: "none",
            }}
          />
        </div>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            marginBottom: 15,
          }}
        >
          {displayedParts.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: 20,
                color: "rgba(0,0,0,0.45)",
                fontSize: 13,
              }}
            >
              Sonuç bulunamadı
            </div>
          ) : (
            displayedParts.map((p) => {
              const id = partIdOf(p);
              const isSelected = selectedIds.includes(id);
              const atMax = selectedIds.length >= 2 && !isSelected;
              return (
                <button
                  key={id || p.code}
                  type="button"
                  disabled={atMax || !id}
                  onClick={() => toggleSelect(id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    padding: "10px 12px",
                    border: isSelected
                      ? "1px solid #4f46e5"
                      : "1px solid #e2e5e8",
                    borderRadius: 6,
                    background: isSelected
                      ? "rgba(79, 70, 229, 0.04)"
                      : "var(--m-card-background-color, #fff)",
                    cursor: atMax ? "not-allowed" : "pointer",
                    opacity: atMax ? 0.5 : 1,
                    textAlign: "left",
                    color: "inherit",
                    transition: "all 0.2s ease",
                  }}
                >
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: 13,
                        color: isSelected ? "#4f46e5" : "inherit",
                      }}
                    >
                      {p.code}
                    </div>
                    {p.name && (
                      <div
                        style={{
                          fontSize: 12,
                          color: "rgba(0,0,0,0.55)",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.name}
                      </div>
                    )}
                  </div>
                  {isSelected && (
                    <span
                      style={{
                        color: "#4f46e5",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <FiCheck size={18} />
                    </span>
                  )}
                </button>
              );
            })
          )}
        </div>

        {totalItems > PAGE_SIZE && (
          <div style={{ marginTop: 10 }}>
            <Pagination
              totalItems={totalItems}
              totalPerPage={PAGE_SIZE}
              currentPage={safePage}
              options={{
                selectQuantity: false,
                simpleCounting: true,
                skipPrevious: true,
                skipNext: true,
                previous: true,
                next: true,
              }}
              onPageChange={({ pagination }) => {
                setPage(pagination?.currentPage || 1);
              }}
            />
          </div>
        )}
      </div>

      <div className={styles.formFooter}>
        <Button
          appearance="subtle"
          type="button"
          onClick={onBack}
          icon={<FiArrowLeft />}
        >
          İptal
        </Button>
        <Button
          appearance="primary-animated"
          type="submit"
          disabled={saving}
          icon={<FiCheck />}
        >
          {saving ? "Kaydediliyor..." : "Seçimi Kaydet"}
        </Button>
      </div>
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// VIEW: Success
// ═══════════════════════════════════════════════════════════════════════════════
function SuccessView({
  machine,
  actionId,
  message,
  onSameAction,
  onChangeAction,
  onChangeMachine,
  onGoToDashboard,
}) {
  const action = ACTIONS.find((a) => a.id === actionId);
  return (
    <div className={styles.successWrap}>
      <div className={styles.successCard}>
        <div className={styles.successHeader}>
          <div className={styles.successIconWrap}>
            <FiCheckCircle size={22} />
          </div>
          <div className={styles.successHeaderText}>
            <h2 className={styles.successTitle}>Kayıt Başarılı</h2>
            {message ? (
              <p className={styles.successMsg}>{message}</p>
            ) : null}
          </div>
        </div>

        <div className={styles.successMeta}>
          <div className={styles.successMetaItem}>
            <span className={styles.successMetaLabel}>Makine</span>
            <span className={styles.successMetaValue}>
              <FiCpu size={14} aria-hidden />
              <span>{machine.name}</span>
            </span>
          </div>
          <div className={styles.successMetaDivider} aria-hidden />
          <div className={styles.successMetaItem}>
            <span className={styles.successMetaLabel}>İşlem</span>
            <span
              className={styles.successMetaValue}
              style={action ? { color: action.color } : undefined}
            >
              {action?.title || "—"}
            </span>
          </div>
        </div>

        <div className={styles.successActions}>
          <Button
            appearance="primary-animated"
            onClick={onSameAction}
            icon={<FiRefreshCw />}
            isBlock
          >
            Tekrar Veri Girişi Yap
          </Button>

          <div className={styles.successActionsDivider} />

          <div className={styles.successActionsSecondary}>
            <div className={styles.successActionsPair}>
              <Button
                appearance="primary-subtle"
                onClick={onChangeAction}
                icon={<FiArrowLeft />}
                isBlock
              >
                Başka İşlem
              </Button>
              <Button
                appearance="warning-subtle"
                onClick={onChangeMachine}
                icon={<FiCpu />}
                isBlock
              >
                Başka Makine
              </Button>
            </div>
            <Button
              appearance="primary-subtle"
              onClick={onGoToDashboard}
              icon={<FiActivity />}
              isBlock
            >
              Dashboard&apos;a Git
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN EPC PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const EPC = () => {
  useTitle("Rovion MES - EPC Veri Girişi");
  const { getToken, getUser } = useAuth();
  const currentUser = getUser();
  const location = useLocation();
  const navigate = useNavigate();

  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/epc",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  const [view, setView] = useState(VIEWS.MACHINES);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [selectedAction, setSelectedAction] = useState(null);
  const [formDefaults, setFormDefaults] = useState(null);

  const [machines, setMachines] = useState([]);
  const [formData, setFormData] = useState(null);
  const [loadingMachines, setLoadingMachines] = useState(true);
  const [loadingFormData, setLoadingFormData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [toasts, setToasts] = useState([]);
  const [machinesPagination, setMachinesPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [machinesSearchQuery, setMachinesSearchQuery] = useState("");
  const [assignmentAccess, setAssignmentAccess] = useState({
    status: "loading",
    unrestricted: false,
    ids: null,
  });

  const allowedMachineIds = useMemo(() => {
    if (assignmentAccess.status !== "ready") return undefined;
    if (assignmentAccess.unrestricted) return null;
    return assignmentAccess.ids ?? new Set();
  }, [assignmentAccess]);

  const machinesEmptyMessage = useMemo(() => {
    if (allowedMachineIds && allowedMachineIds.size === 0) {
      return "Size atanmış makine bulunamadı.";
    }
    if (machinesSearchQuery.trim()) {
      return "Aramanızla eşleşen makine bulunamadı.";
    }
    return "Görüntülenecek makine bulunamadı.";
  }, [allowedMachineIds, machinesSearchQuery]);

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${getToken()}` }),
    [getToken],
  );

  useEffect(() => {
    let cancelled = false;
    setAssignmentAccess({ status: "loading", unrestricted: false, ids: null });

    axios
      .get(`${API_URL}/api/epc/assigned-machine-ids`, { headers: headers() })
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.unrestricted) {
          setAssignmentAccess({
            status: "ready",
            unrestricted: true,
            ids: null,
          });
          return;
        }
        setAssignmentAccess({
          status: "ready",
          unrestricted: false,
          ids: new Set((data?.machineIds || []).map((id) => String(id))),
        });
      })
      .catch(() => {
        if (!cancelled) {
          setAssignmentAccess({
            status: "ready",
            unrestricted: false,
            ids: new Set(),
          });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentUser, headers]);

  const dismissToast = useCallback(
    (id) => setToasts((p) => p.filter((t) => t.id !== id)),
    [],
  );
  const addToast = useCallback((message, type = "success") => {
    setToasts((p) => [...p, { id: Date.now() + Math.random(), message, type }]);
  }, []);

  // ── Fetch machines ───────────────────────────────────────────────────────────
  const fetchMachines = useCallback(
    async (pageVal = 1, limitVal = 20, searchVal = "") => {
      if (allowedMachineIds === undefined) return;

      setLoadingMachines(true);
      try {
        let url = `${API_URL}/api/epc/machines?page=${pageVal}&limit=${limitVal}`;
        if (searchVal.trim()) {
          url += `&search=${encodeURIComponent(searchVal.trim())}`;
        }

        const { data } = await axios.get(url, { headers: headers() });
        const rawMachines = data.machines || [];
        const list =
          allowedMachineIds === null || allowedMachineIds === undefined
            ? rawMachines
            : filterMachinesByAccess(rawMachines, allowedMachineIds);
        setMachines(list);
        if (data.pagination) {
          setMachinesPagination({
            page: data.pagination.page || pageVal,
            limit: data.pagination.limit || limitVal,
            total: data.pagination.total || 0,
            totalPages: data.pagination.totalPages || 0,
            hasNextPage: data.pagination.hasNextPage || false,
            hasPrevPage: data.pagination.hasPrevPage || false,
          });
        }
      } catch {
        addToast("Makineler yüklenemedi", "danger");
      } finally {
        setLoadingMachines(false);
      }
    },
    [headers, addToast, allowedMachineIds],
  );

  useEffect(() => {
    if (allowedMachineIds === undefined) {
      setLoadingMachines(true);
      return;
    }
    fetchMachines(
      machinesPagination.page,
      machinesPagination.limit,
      machinesSearchQuery,
    );
  }, [
    machinesPagination.page,
    machinesPagination.limit,
    machinesSearchQuery,
    fetchMachines,
    allowedMachineIds,
  ]);

  const ensureMachineAllowed = useCallback(
    (machine) => {
      if (!isMachineAllowed(machine?._id, allowedMachineIds)) {
        addToast("Bu makine için yetkiniz bulunmuyor", "danger");
        return false;
      }
      return true;
    },
    [allowedMachineIds, addToast],
  );

  // ── Select machine → show actions ────────────────────────────────────────────
  const handleSelectMachine = (machine) => {
    if (!ensureMachineAllowed(machine)) return;
    setSelectedMachine(machine);
    setSelectedAction(null);
    setFormData(null);
    setView(VIEWS.ACTIONS);
  };

  const fetchStopFormData = useCallback(
    async (machineId, dateVal = todayStr()) => {
      const params = new URLSearchParams({
        machineId: String(machineId),
        action: "stop",
      });
      if (dateVal) params.set("date", dateVal);
      const { data } = await axios.get(
        `${API_URL}/api/epc/form-data?${params}`,
        { headers: headers() },
      );
      setFormData(data);
    },
    [headers],
  );

  const handleStopDateChange = useCallback(
    async (dateVal) => {
      if (!selectedMachine?._id) return;
      setLoadingFormData(true);
      try {
        await fetchStopFormData(selectedMachine._id, dateVal);
      } catch {
        addToast("Duruş kayıtları yüklenemedi", "danger");
      } finally {
        setLoadingFormData(false);
      }
    },
    [selectedMachine?._id, fetchStopFormData, addToast],
  );

  // ── Select machine and action directly ────────────────────────────────────────
  const handleSelectMachineAndAction = async (machine, actionId, opts = {}) => {
    if (!ensureMachineAllowed(machine)) return;
    setSelectedMachine(machine);
    setSelectedAction(actionId);
    setLoadingFormData(true);
    setView(VIEWS.FORM);
    try {
      if (actionId === "stop") {
        await fetchStopFormData(machine._id, opts.date || todayStr());
      } else {
        const { data } = await axios.get(
          `${API_URL}/api/epc/form-data?machineId=${machine._id}&action=${actionId}`,
          { headers: headers() },
        );
        setFormData(data);
      }
    } catch {
      addToast("Form verileri yüklenemedi", "danger");
      setView(VIEWS.ACTIONS);
    } finally {
      setLoadingFormData(false);
    }
  };

  const handleSelectAction = async (actionId) => {
    setSelectedAction(actionId);
    setLoadingFormData(true);
    setView(VIEWS.FORM);
    try {
      if (actionId === "stop") {
        await fetchStopFormData(selectedMachine._id);
      } else {
        const { data } = await axios.get(
          `${API_URL}/api/epc/form-data?machineId=${selectedMachine._id}&action=${actionId}`,
          { headers: headers() },
        );
        setFormData(data);
      }
    } catch {
      addToast("Form verileri yüklenemedi", "danger");
      setView(VIEWS.ACTIONS);
    } finally {
      setLoadingFormData(false);
    }
  };

  useEffect(() => {
    const machine = location.state?.machine;
    const action = location.state?.action;
    if (!machine) return;

    setFormDefaults({
      stopLogId: location.state?.stopLogId || null,
      stopDate: location.state?.stopDate || null,
      shiftId: location.state?.shiftId || null,
      partId: location.state?.partId || null,
    });

    navigate(location.pathname, { replace: true, state: {} });

    if (action) {
      handleSelectMachineAndAction(machine, action, {
        date: location.state?.stopDate || undefined,
      });
    } else {
      handleSelectMachine(machine);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, location.pathname]);

  // ── Submit handlers ───────────────────────────────────────────────────────
  const handleSubmit = async (payload, actionOverride) => {
    if (!canWrite) {
      addToast("Bu işlem için yetkiniz yok", "danger");
      return;
    }
    setSaving(true);
    try {
      const actionPath = actionOverride || selectedAction;
      const { data } = await axios.post(
        `${API_URL}/api/epc/${actionPath}`,
        payload,
        { headers: headers() },
      );
      setSuccessMsg(data.message || "Kayıt başarılı");
      setView(VIEWS.SUCCESS);
    } catch (err) {
      addToast(err?.response?.data?.message || "Kayıt başarısız", "danger");
    } finally {
      setSaving(false);
    }
  };

  // ── Navigation helpers ────────────────────────────────────────────────────
  const goToMachines = () => {
    setView(VIEWS.MACHINES);
    setSelectedMachine(null);
    setSelectedAction(null);
    setFormData(null);
    setFormDefaults(null);
  };
  const goToActions = () => {
    setView(VIEWS.ACTIONS);
    setSelectedAction(null);
    setFormData(null);
    setFormDefaults(null);
  };
  const goToForm = () => handleSelectAction(selectedAction); // re-fetches & shows form
  const goBackFromForm = () => setView(VIEWS.ACTIONS);

  // ── Page subtitle ─────────────────────────────────────────────────────────
  const getSubtitle = () => {
    if (view === VIEWS.MACHINES) return "Bağlanmak istediğiniz makineyi seçin";
    if (view === VIEWS.ACTIONS)
      return "Gerçekleştirmek istediğiniz işlemi seçin";
    const action = ACTIONS.find((a) => a.id === selectedAction);
    return action ? action.title : "Form";
  };

  return (
    <AppPage title="EPC" subtitle={getSubtitle()} icon={<FiCpu />}>
      <ToastContainer>
        {toasts.map((t) => (
          <Toast
            key={t.id}
            title={t.message}
            appearance={t.type === "danger" ? "danger" : "success"}
            onClose={() => dismissToast(t.id)}
          />
        ))}
      </ToastContainer>

      {/* MACHINES */}
      {view === VIEWS.MACHINES && (
        <MachinesView
          machines={machines}
          loading={loadingMachines || allowedMachineIds === undefined}
          onSelectActionDirectly={handleSelectMachineAndAction}
          pagination={machinesPagination}
          onPageChange={(p) =>
            setMachinesPagination((prev) => ({ ...prev, page: p }))
          }
          searchQuery={machinesSearchQuery}
          onSearchChange={(q) => {
            setMachinesSearchQuery(q);
            setMachinesPagination((prev) => ({ ...prev, page: 1 }));
          }}
          emptyMessage={machinesEmptyMessage}
          onRedirectToDashboard={(m) =>
            navigate("/machine-monitor", { state: { machineId: m._id } })
          }
        />
      )}

      {/* ACTIONS */}
      {view === VIEWS.ACTIONS && selectedMachine && (
        <ActionsView
          machine={selectedMachine}
          onSelect={handleSelectAction}
          onBack={goToMachines}
        />
      )}

      {/* FORM */}
      {view === VIEWS.FORM && selectedMachine && (
        <>
          <MachineHeader
            machine={selectedMachine}
            step={3}
            onBack={goBackFromForm}
          />
          {loadingFormData ? (
            <div className={styles.loadingForm}>
              <FiRefreshCw className={styles.spin} size={22} />
              <span>Form yükleniyor...</span>
            </div>
          ) : (
            <>
              {selectedAction === "production" && (
                <ProductionForm
                  machine={selectedMachine}
                  formData={formData}
                  onSubmit={handleSubmit}
                  saving={saving}
                  onBack={goBackFromForm}
                />
              )}
              {selectedAction === "scrap" && (
                <ScrapForm
                  machine={selectedMachine}
                  formData={formData}
                  authHeaders={headers}
                  initialShiftId={formDefaults?.shiftId}
                  initialPartId={formDefaults?.partId}
                  onSubmit={handleSubmit}
                  saving={saving}
                  onBack={goBackFromForm}
                />
              )}
              {selectedAction === "stop" && (
                <StopForm
                  machine={selectedMachine}
                  formData={formData}
                  initialStopLogId={formDefaults?.stopLogId}
                  onSubmit={handleSubmit}
                  saving={saving}
                  onBack={goBackFromForm}
                  onDateChange={handleStopDateChange}
                  loadingStops={loadingFormData}
                />
              )}
              {selectedAction === "part_select" && (
                <PartSelectForm
                  machine={selectedMachine}
                  formData={formData}
                  onSubmit={handleSubmit}
                  saving={saving}
                  onBack={goBackFromForm}
                />
              )}
            </>
          )}
        </>
      )}

      {/* SUCCESS */}
      {view === VIEWS.SUCCESS && selectedMachine && (
        <SuccessView
          machine={selectedMachine}
          actionId={selectedAction}
          message={successMsg}
          onSameAction={goToForm}
          onChangeAction={goToActions}
          onChangeMachine={goToMachines}
          onGoToDashboard={() =>
            navigate("/machine-monitor", {
              state: { machineId: selectedMachine._id },
            })
          }
        />
      )}
    </AppPage>
  );
};

export default withAuth(EPC);
